/*
 * ========= WARNING =========
 * This files contains shared utils used by the CodeIsland Atom
 * and CodeIslandCustomElement. Any packages imported here will be included in the
 * client bundle, so be careful what you add here!
 */
import {type Json, getObject, getString} from '@packages/utilities/json';
import {trackEvent} from '@packages/utilities/analytics';
import {ANALYTICS_EVENTS} from '@packages/systems/core/constants/analytics';
import {
  CodeModuleLoaderType,
  FederationLoaderConfig,
  CodeModuleLoaderConfig,
  SiteLoaderConfig,
  NullLoaderConfig,
} from '@packages/systems/code-modules/core';
import type {FunctionsReadiness} from '@packages/systems/code-modules/constants/functions';

/**
 *  Eventually we can replace these with the types exported from @webflow/code.
 */

export enum Frameworks {
  React = 'React',
}

/**
 * Interface for component definitions returned by loaders
 */
export interface IComponentDefinition<T = unknown> {
  component: T;
  framework: Frameworks;
  stylesheets: string[];
  name?: string;
}

/**
 * Interface for renderer root objects
 */
export interface IRendererRoot {
  [key: string]: unknown;
}

/**
 * Interface for renderer node objects
 */
export interface IRendererNode {
  [key: string]: unknown;
}

/**
 * Interface for renderer options
 */
type IRendererComponentError = {
  error: Error;
  message: string;
  stack?: string;
  code?: string;
};
/**
 * The half of the context that is serialized into the `data-webflow-context`
 * attribute, which anything on the page can read. Nothing secret belongs here.
 */
export type IRendererPublicWebflowContext = {
  mode:
    | 'design'
    | 'build'
    | 'edit'
    | 'preview'
    | 'component-preview'
    | 'comment'
    | 'analyze'
    | 'publish';
  interactive: boolean;
  locale: null | string;
  functions?: {baseUrl?: string};
};

/**
 * The `functions` config the renderer receives. `baseUrl` is absent while the
 * Designer has no usable preview credentials; `readiness` says whether waiting
 * can still produce them. A present `baseUrl` always wins over `readiness` —
 * see `isFunctionsPending` in `@webflow/react`.
 */
export type IRendererFunctionsContext = {
  baseUrl?: string;
  headers?: Record<string, string>;
  readiness?: FunctionsReadiness;
};

/**
 * The half the renderer needs but that must never reach the DOM. It is handed
 * to the code island element as a JS property and merged back in at render
 * time. Add new secrets here rather than to the public half.
 *
 * The Designer preview puts its whole `functions` config here, base URL
 * included, so a component can never observe a base URL without the token that
 * authorizes it.
 */
export type IRendererPrivateWebflowContext = {
  functions?: IRendererFunctionsContext;
};

export type IRendererWebflowContext = IRendererPublicWebflowContext &
  IRendererPrivateWebflowContext;

export interface IRendererOptions {
  onError: (error: IRendererComponentError) => void;
  // Fired once each `render` commits, including `return null`. Renderers
  // bundled before `@webflow/react` added it ignore it.
  onRendered?: () => void;
  webflowContext: IRendererWebflowContext;
  data?: Readonly<Record<string, unknown>>;
}

/**
 * Interface for renderers that can create, mount and render components
 */
export interface IRenderer<TProps = unknown> {
  mount: (el: Element) => IRendererRoot;
  render: (
    root: IRendererRoot,
    props: TProps,
    options?: IRendererOptions
  ) => void;
  hydrate: (
    domNode: Element,
    props?: TProps,
    options?: IRendererOptions
  ) => IRendererRoot;
  createSlot: (name: string) => IRendererNode;
}

/**
 * Interface for renderer factory functions
 */
export interface IRendererFactory<TComponent = unknown, TProps = unknown> {
  (component: TComponent): IRenderer<TProps>;
}

/**
 * Interface for render modules that provide renderer factories
 */
export interface IRenderModule<TComponent = unknown, TProps = unknown> {
  Renderer: IRendererFactory<TComponent, TProps>;
  env: 'Client' | 'Server';
}

/**
 * Interface for loaders that can load components and renderers
 */
export interface ILoader {
  register: () => Promise<void>;
  // A null definition is still awaiting compilation in the site bundle.
  loadComponentDefinition: () => Promise<IComponentDefinition | null>;
  loadRenderer: () => Promise<IRendererFactory>;
}

const CODE_ISLAND_PRERENDER_DATA_SCRIPT_ATTR =
  'data-wf-code-island-prerender-data';

const CODE_ISLAND_PRERENDER_DATA_SCRIPT_SELECTOR = `script[type="application/json"][${CODE_ISLAND_PRERENDER_DATA_SCRIPT_ATTR}]`;

/**
 * Checks if the data is a valid prerender data object.
 */
function isValidPrerenderData(data: unknown): data is Record<string, unknown> {
  return (
    data != null &&
    typeof data === 'object' &&
    !Array.isArray(data) &&
    Object.keys(data).length > 0
  );
}

/**
 * HTML fragment embedded in the code-island shadow root before `[data-root]`
 * when prerender returned non-empty `data`.
 */
export function buildCodeIslandPrerenderDataScript(data: unknown): string {
  if (!isValidPrerenderData(data)) {
    return '';
  }

  try {
    const json = JSON.stringify(data).replace(/</g, '\\u003c');
    return `<script type="application/json" ${CODE_ISLAND_PRERENDER_DATA_SCRIPT_ATTR}>${json}</script>`;
  } catch {
    return '';
  }
}

/**
 * Reads and validates the SSR prerender-data payload from the shadow root.
 */
export function parseCodeIslandPrerenderDataScript(
  shadowRoot: ShadowRoot | null | undefined
): Record<string, unknown> | undefined {
  if (!shadowRoot) {
    return undefined;
  }
  const el = shadowRoot.querySelector(
    CODE_ISLAND_PRERENDER_DATA_SCRIPT_SELECTOR
  );
  if (!el || !el.textContent) {
    return undefined;
  }
  try {
    const parsed: unknown = JSON.parse(el.textContent);
    return isValidPrerenderData(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
}

export type BundleManifest = {
  version: number;
  type: CodeModuleLoaderType;
  entry: string;
  renderer: string;
};

export type CodeIslandSize = {
  width: number;
  height: number;
};

export type CodeIslandState =
  | {
      status: 'loading';
      loaderType?: CodeModuleLoaderType;
    }
  | {
      status: 'error';
      error: Error;
      loaderType?: CodeModuleLoaderType;
    }
  | {
      status: 'loaded';
      key?: string;
      loaderType?: CodeModuleLoaderType;
    };

export function getLoader(maybeLoader: Json): CodeModuleLoaderConfig | null {
  const loader = getObject(maybeLoader);
  if (!loader) return null;
  const loaderOpts = loader.val ? getObject(loader.val) : null;
  if (!loaderOpts) return null;
  const loaderType = loader.tag ? getString(loader.tag) : null;

  if (loaderType === CodeModuleLoaderType.FEDERATION) {
    const clientModuleUrl = getFieldAsStringOrNull(loaderOpts.clientModuleUrl);
    const moduleId = getFieldAsStringOrNull(loaderOpts.moduleId);
    const submoduleId = getFieldAsStringOrNull(loaderOpts.submoduleId);
    const exportPath = getFieldAsStringOrNull(loaderOpts.exportPath);
    if (
      clientModuleUrl === null ||
      moduleId === null ||
      submoduleId === null ||
      exportPath === null
    ) {
      return null;
    }

    // For backwards compatibility with existing published sites
    // we're injecting an empty string as the serverModuleUrl.
    // This value is not used anywhere but in older version of
    // siteModules we're checking for this value existence nevertheless
    // so removing it will crash published sites.
    loaderOpts.serverModuleUrl = '_';

    return {
      tag: loader.tag,
      val: loaderOpts,
    } as FederationLoaderConfig;
  }

  if (loaderType === CodeModuleLoaderType.SITE) {
    const componentId = getFieldAsStringOrNull(loaderOpts.componentId);
    if (componentId === null) {
      return null;
    }
    return loader as SiteLoaderConfig;
  }

  if (loaderType === CodeModuleLoaderType.NULL) {
    return loader as NullLoaderConfig;
  }

  return null;
}

export function getFieldAsStringOrNull(
  maybeField: Json | undefined
): string | null {
  if (maybeField === undefined) return null;
  const field = getString(maybeField);
  if (field === undefined) return null;
  return field;
}

export function parseNativeIdPath(nativeIdPath: string): {
  symbolId: string | null;
  instanceId: string | null;
} {
  try {
    const nativeIdArray = JSON.parse(nativeIdPath);
    if (Array.isArray(nativeIdArray) && nativeIdArray.length > 0) {
      const symbolId = nativeIdArray[nativeIdArray.length - 1];
      const instanceId = nativeIdPath; // hash it?
      return {symbolId, instanceId};
    }
  } catch (e) {
    // If parsing fails, return null
  }
  return {symbolId: null, instanceId: null};
}

function isInIframe(): boolean {
  return typeof window !== 'undefined' && window.top !== window.self;
}

function isInCrossOriginParentIframe(): boolean {
  if (typeof window === 'undefined' || window.parent === window) {
    return false;
  }
  try {
    // Will throw a DOMException if parent is cross-origin
    const _ = window.parent.document;
    return false; // same-origin parent
  } catch {
    return true; // cross-origin parent
  }
}

export function reportDesignerError(
  name: string,
  message: string,
  metadata?: Record<string, unknown>
) {
  if (isInIframe() && isInCrossOriginParentIframe()) {
    // in custom code iframe
    const messageData = {
      method: 'telemetry.sendError',
      name: `canvasApp - ${name}`,
      data: message,
    };
    window.parent?.postMessage(messageData, '*');
    return;
  } else if (isInIframe() && window.parent?._reportBug) {
    // in preview iframe
    window.parent?._reportBug(name, message, metadata);
    // Also track error in segment for reporting purposes
    trackEvent({
      name: ANALYTICS_EVENTS.CODE_COMPONENT_INSTANCE_ERRORED,
      data: {
        ...(metadata ? metadata : {}),
        error: message,
      },
    });
    return;
  } else if (typeof window !== 'undefined' && window._reportBug) {
    // in designer window
    window._reportBug(name, message, metadata);
    // Also track error in segment for reporting purposes
    trackEvent({
      name: ANALYTICS_EVENTS.CODE_COMPONENT_INSTANCE_ERRORED,
      data: {
        ...(metadata ? metadata : {}),
        error: message,
      },
    });
    return;
  }

  // if not in designer context fallback to log to console
  console.error(name, message, metadata);
}

export function requestFixWithAI(errorMessage?: string) {
  if (typeof window === 'undefined') return;

  let payload = 'Identify and fix the errors';
  if (errorMessage) {
    payload += `:\n\n${errorMessage}`;
  } else {
    payload += '.';
  }
  const messageData = {
    method: 'dispatch',
    type: 'CODE_COMPONENT_CREATOR_PROMPT_SET',
    payload,
  };
  if (isInIframe()) {
    window.parent?.postMessage(messageData, '*');
  } else {
    window.postMessage(messageData, window.location.origin);
  }
}

// display: contents makes the wrapper invisible to layout
export const DISPLAY_CONTENTS_STYLE = {display: 'contents'} as const;

function isQuotaExceededError(e: unknown): boolean {
  if (typeof e !== 'object' || e === null) return false;
  const {name, code} = e as {name?: unknown; code?: unknown};
  return (
    name === 'QuotaExceededError' ||
    // Legacy Firefox/Gecko quota-exceeded signature (pre-standardization)
    name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
    code === 22 ||
    code === 1014
  );
}

let lastForcedPruneAt = 0;
// Bounds how often a persistently-full origin re-triggers a full localStorage
// scan from repeated quota failures (e.g. storeLastKnownSize firing on every
// resize) once a force-prune has already found nothing left to reclaim.
const FORCED_PRUNE_COOLDOWN_MS = 30 * 1000;

// Reported once per session so a persistently unavailable localStorage
// (privacy mode, storage disabled) doesn't fire on every write attempt.
let hasReportedStorageUnavailable = false;

/**
 * reportDesignerError has no internal try/catch, and its downstream calls
 * (window._reportBug, trackEvent -> analytics.track) are unguarded — a
 * reporter failure must never itself throw here, especially from the quota
 * branch, where other same-origin storage-backed code is also likely to be
 * failing for the same reason.
 */
function safeReportDesignerError(
  name: string,
  message: string,
  metadata?: Record<string, unknown>
): void {
  try {
    reportDesignerError(name, message, metadata);
  } catch {
    // Best-effort: reporting a storage failure must never itself throw
  }
}

// Reports an unavailable-storage give-up at most once per session, whether
// localStorage is entirely absent or throws on access/write.
function reportStorageUnavailableOnce(): void {
  if (hasReportedStorageUnavailable) return;
  hasReportedStorageUnavailable = true;
  safeReportDesignerError(
    'CodeIslandStorageWriteFailed',
    'localStorage write failed',
    {reason: 'unavailable'}
  );
}

/**
 * Best-effort localStorage write. On quota errors, prunes stale code-island
 * keys and retries once; any other error (privacy mode, storage disabled) is
 * swallowed. Never throws — a crash here would take down the whole Designer.
 * Returns whether the value was actually persisted.
 *
 * The Datadog RUM crash query this fix targets can't tell "fixed" apart from
 * "silently dropping writes for a cohort still at quota" — report the give-up
 * paths (bounded/deduped) via reportDesignerError so that's visible. Outside a
 * Designer/preview context it makes no network call (it falls back to a single
 * bounded console.error), so it stays safe to call from the published-site
 * runtime this file also ships to.
 */
function safeSetItem(key: string, value: string): boolean {
  try {
    // Access inside the try: reading window.localStorage can itself throw
    // (SecurityError) in some privacy modes.
    const storage = window.localStorage;
    if (!storage) {
      reportStorageUnavailableOnce();
      return false;
    }
    storage.setItem(key, value);
    return true;
  } catch (e) {
    if (isQuotaExceededError(e)) {
      const now = Date.now();
      const didForcePrune = now - lastForcedPruneAt >= FORCED_PRUNE_COOLDOWN_MS;
      if (didForcePrune) {
        lastForcedPruneAt = now;
        pruneCodeIslandStorage({force: true});
      }
      try {
        const storage = window.localStorage;
        if (storage) {
          storage.setItem(key, value);
          return true;
        }
      } catch {
        // Retry failed too — give up silently, but surface it if we just
        // pruned (bounded to the cooldown above) so persistent quota
        // exhaustion doesn't look identical to "fixed" in telemetry.
        if (didForcePrune) {
          safeReportDesignerError(
            'CodeIslandStorageWriteFailed',
            'localStorage quota exceeded and remained full after pruning',
            {reason: 'quota'}
          );
        }
      }
    } else {
      reportStorageUnavailableOnce();
    }
    return false;
  }
}

function safeGetItem(key: string): string | null {
  try {
    return window.localStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function safeRemoveItem(key: string): void {
  try {
    window.localStorage?.removeItem(key);
  } catch {
    // Best-effort
  }
}

/**
 * Render checkpoints are used to verify that the last render was successful
 * and to prevent hanging components from breaking the site
 */
export function storeRenderCheckpoint(
  instanceId: string | null | undefined,
  type: 'start' | 'end'
): void {
  if (!instanceId) return;
  const persisted = safeSetItem(
    `code-island-${instanceId}-${type}`,
    Date.now().toString()
  );
  if (type === 'end') {
    // 'end' fires on both success and a handled render error — either way the
    // render didn't hang, so the consecutive-hang-failure record no longer applies.
    safeRemoveItem(`code-island-${instanceId}-failed-renders`);
    if (!persisted) {
      // The end write didn't persist (quota). Drop the now-orphaned start so
      // the next load reads a first render instead of a false hang for a
      // render that actually completed.
      safeRemoveItem(`code-island-${instanceId}-start`);
    }
  }
}

export function clearRenderCheckpoints(
  instanceId: string | null | undefined
): void {
  if (!instanceId) return;
  safeRemoveItem(`code-island-${instanceId}-start`);
  safeRemoveItem(`code-island-${instanceId}-end`);
}

export function getLastKnownSizeStorageKey(instanceId: string): string {
  return `code-island-${instanceId}-size`;
}

// Persisted sizes older than this are ignored — the component likely changed
const LAST_KNOWN_SIZE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

// Stale start/end checkpoints beyond this are leftovers from a render that
// never finished (e.g. a tab closed mid-render) — the crash signal only
// matters on the next load shortly after a crash
const RENDER_CHECKPOINT_TTL_MS = 60 * 60 * 1000;

let hasPrunedThisSession = false;
let hasScheduledIdlePrune = false;

/**
 * Schedules the once-per-session sweep to run when the browser is idle rather
 * than synchronously. The scan reads every key on the origin, and the origins
 * this targets are near quota — doing it inline would land the heaviest scan
 * on the canvas-mount critical path. The forced (quota-retry) path stays
 * synchronous; only this opportunistic sweep is deferred.
 */
function scheduleCodeIslandStoragePrune(): void {
  if (hasPrunedThisSession || hasScheduledIdlePrune) return;
  hasScheduledIdlePrune = true;
  const run = () => pruneCodeIslandStorage();
  if (typeof requestIdleCallback === 'function') {
    // timeout so a backgrounded tab or sustained main-thread work can't starve
    // the sweep indefinitely — hasScheduledIdlePrune blocks a second attempt,
    // so this one callback has to fire for the session to ever get pruned.
    requestIdleCallback(run, {timeout: 2000});
  } else {
    // Safari < 16.4 has no requestIdleCallback
    setTimeout(run, 0);
  }
}

/**
 * Removes stale code-island-* localStorage keys so Code Islands don't
 * contribute to filling up the shared Designer-origin quota. Runs at most
 * once per page session unless `force` is set (e.g. on a quota error).
 */
export function pruneCodeIslandStorage({force}: {force?: boolean} = {}): void {
  if (hasPrunedThisSession && !force) return;
  hasPrunedThisSession = true;

  try {
    const storage = window.localStorage;
    if (!storage) return;

    const keysToRemove: string[] = [];
    const now = Date.now();

    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (!key || !key.startsWith('code-island-')) continue;

      if (key.endsWith('-failed-renders')) {
        const raw = storage.getItem(key);
        const timestamp = raw ? Number(raw) : NaN;
        if (
          !Number.isFinite(timestamp) ||
          now - timestamp > FAILED_RENDER_TIMESPAN
        ) {
          keysToRemove.push(key);
        }
      } else if (key.endsWith('-size')) {
        if (!parseStoredSize(storage.getItem(key))) {
          keysToRemove.push(key);
        }
      } else if (key.endsWith('-start') || key.endsWith('-end')) {
        const raw = storage.getItem(key);
        const timestamp = raw ? Number(raw) : NaN;
        if (
          !Number.isFinite(timestamp) ||
          now - timestamp > RENDER_CHECKPOINT_TTL_MS
        ) {
          keysToRemove.push(key);
        }
      }
    }

    for (const key of keysToRemove) {
      storage.removeItem(key);
    }
  } catch {
    // Best-effort: storage access can throw (privacy mode, storage disabled)
  }
}

// Size-record id shared by all instances of a component, so a never-rendered
// instance can fall back to a sibling instance's size
export function getCodeIslandDefinitionId(
  loader: CodeModuleLoaderConfig | null
): string | null {
  if (!loader) return null;
  if (loader.tag === CodeModuleLoaderType.FEDERATION) {
    // ':' cannot appear in module ids, so the join is unambiguous
    return `def-${loader.val.moduleId}:${loader.val.submoduleId}`;
  }
  if (loader.tag === CodeModuleLoaderType.SITE) {
    return `def-${loader.val.componentId}`;
  }
  return null;
}

// Persisted so the loading placeholder can match the size across sessions
export function storeLastKnownSize(
  instanceId: string | null | undefined,
  size: CodeIslandSize
): void {
  if (!instanceId) return;
  safeSetItem(
    getLastKnownSizeStorageKey(instanceId),
    JSON.stringify({...size, t: Date.now()})
  );
}

export function parseStoredSize(
  raw: string | null | undefined
): CodeIslandSize | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed.width === 'number' &&
      parsed.width > 0 &&
      typeof parsed.height === 'number' &&
      parsed.height > 0
    ) {
      if (
        typeof parsed.t === 'number' &&
        Date.now() - parsed.t > LAST_KNOWN_SIZE_TTL_MS
      ) {
        return null;
      }
      return {width: parsed.width, height: parsed.height};
    }
  } catch {
    // Corrupt entry — treat as absent
  }
  return null;
}

// 10 min
const FAILED_RENDER_TIMESPAN = 10 * 60 * 1000;

export function verifyRenderCheckpoints(
  instanceId: string | null | undefined
): boolean {
  if (!instanceId) return true;
  scheduleCodeIslandStoragePrune();
  const start = safeGetItem(`code-island-${instanceId}-start`);
  const end = safeGetItem(`code-island-${instanceId}-end`);
  if (start && end) {
    // last render was successful
    return true;
  }
  if (!start && !end) {
    // first render
    return true;
  }
  if (start && !end) {
    // last render was not successful
    // if we have failed more than once in the FAILED_RENDER_TIMESPAN, we should show the error state
    const lastFailedRender = recordFailedRender(instanceId);
    return lastFailedRender
      ? Date.now() - lastFailedRender.getTime() > FAILED_RENDER_TIMESPAN
      : true;
  }

  return true;
}

function recordFailedRender(
  instanceId: string | null | undefined
): Date | undefined {
  if (!instanceId) return;

  const lastFailedRender =
    safeGetItem(`code-island-${instanceId}-failed-renders`) || null;

  safeSetItem(
    `code-island-${instanceId}-failed-renders`,
    Date.now().toString()
  );

  // new Date(string) parses via Date.parse, not as an epoch-ms number, so a
  // plain numeric string (as stored above) would otherwise yield Invalid Date.
  return lastFailedRender ? new Date(Number(lastFailedRender)) : undefined;
}
